import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, InputAdornment, MenuItem, Slider, TextField, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';

/**
 * "Navigation menu" card. Decides which categories appear in the top menu
 * and how the menu is dimensioned. All copy via the i18n `t` prop with
 * English fallbacks.
 */
const NavigationMenuCard = ({
    value = {},
    onChange,
    availableCategories = [],
    theme,
    t,
}) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    return (
        <ConfigCard
            title={tr('cfg_nav_menu_title', 'Navigation menu')}
            description={tr('cfg_nav_menu_desc', 'Pick which categories show in the top navigation.')}
            theme={theme}
        >
            <Autocomplete
                multiple
                freeSolo
                size="small"
                options={availableCategories.map(c => c.id ?? c)}
                value={Array.isArray(get('categoriesShown')) ? get('categoriesShown') : []}
                onChange={(_e, v) => set('categoriesShown')(v)}
                getOptionLabel={(opt) => {
                    const match = availableCategories.find(c => (c.id ?? c) === opt);
                    return match ? (match.name || match.id || String(opt)) : String(opt);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={tr('cfg_nav_menu_categories', 'Categories shown in the menu')}
                        placeholder={tr('cfg_nav_menu_categories_placeholder', 'Add a category')}
                    />
                )}
            />

            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {tr('cfg_nav_menu_items_per_row', 'Items per row')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Slider
                        size="small"
                        min={1}
                        max={12}
                        step={1}
                        value={Number(get('itemsPerRow', 6)) || 6}
                        onChange={(_e, v) => set('itemsPerRow')(v)}
                        valueLabelDisplay="auto"
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        value={get('itemsPerRow', 6) ?? 6}
                        onChange={(e) => set('itemsPerRow')(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))}
                        InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_nav_menu_items_unit', 'items')}</InputAdornment> }}
                        sx={{ width: 110 }}
                    />
                </Box>
            </Box>

            <TextField
                type="number"
                size="small"
                label={tr('cfg_nav_menu_height', 'Menu height')}
                value={get('menuHeight', 50) ?? 50}
                onChange={(e) => set('menuHeight')(Math.max(20, parseInt(e.target.value) || 0))}
                InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
            />

            <TextField
                select
                size="small"
                label={tr('cfg_nav_menu_text_size', 'Menu text size')}
                value={String(get('menuTextSize', 14)) || '14'}
                onChange={(e) => set('menuTextSize')(parseInt(e.target.value))}
            >
                {[10, 11, 12, 13, 14, 15, 16, 18, 20].map((sz) => (
                    <MenuItem key={sz} value={String(sz)}>{sz}px</MenuItem>
                ))}
            </TextField>
        </ConfigCard>
    );
};

NavigationMenuCard.propTypes = {
    value:               PropTypes.object,
    onChange:            PropTypes.func,
    availableCategories: PropTypes.array,
    theme:               PropTypes.string,
    t:                   PropTypes.func,
};

export default NavigationMenuCard;
